import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { checkModuleSlug } from "../../../api/client";
import { CategoryEnum, ModuleI, ModuleStatus } from "../../../types";
import validateModule from "../utils/validateTopic";
import MultiSelectDropdown from "../../../componentLibrary/MultiSelectDropdown";
import Container from "../../../componentLibrary/Container";
import Button from "../../../componentLibrary/Button";
import Input from "../../../componentLibrary/Input";
import Card from "../../../componentLibrary/Card";
import Select, { SelectOption } from "../../../componentLibrary/Select";
import ExternalTextEditor from "../../../componentLibrary/ExternalTextEditor";
import { useNotification } from "../../../hooks/useNotification";
import { useCreateTopic } from "../../../api/mutations";
import { ImageTypeEnum } from "../../../types";
import { useSlugValidation } from "../../../hooks/useSlugValidation";
import SlugInputField from "../../../componentLibrary/SlugInputField";
import ReorderableVerticalList from "../../../componentLibrary/ReorderableVerticalList";
import RbdFullRowDragSurface from "../../../componentLibrary/RbdFullRowDragSurface";
import LessonSelect from "./LessonSelect";
import IconSelect from "./IconSelect";
import ColorSelect from "./ColorSelect";

const blankModule = {
  title: "",
  subtitle: "",
  description: "",
  color: "#e1395b",
  icon: {
    _id: "",
    src: "",
    key: "",
    name: "",
    alt: "",
    type: ImageTypeEnum.ICON,
  },
  lessons: [],
  categories: [],
  status: ModuleStatus.Draft,
  trailerVideoUrl: "",
  trailerTranscript: "",
};

interface AddModuleComponentProps {
  canCreateContent?: boolean;
  /** Called after successful module creation to close the add panel and show the editor */
  setShowAddModulePanel?: React.Dispatch<React.SetStateAction<boolean>>;
  /** Called with the created module so the content manager opens it */
  setModuleData?: React.Dispatch<React.SetStateAction<ModuleI | undefined>>;
}

function AddTopicComponent({
  canCreateContent = false,
  setShowAddModulePanel,
  setModuleData,
}: AddModuleComponentProps) {
  const [newModule, setNewModule] = useState<ModuleI>(blankModule);
  const {
    status: slugStatus,
    validate: validateSlug,
    reset: resetSlugStatus,
  } = useSlugValidation(checkModuleSlug);

  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const mutation = useCreateTopic({
    onSuccess: async (data: unknown) => {
      const created = data as ModuleI | undefined;
      setShowAddModulePanel?.(false);
      notify("success", "Module created successfully");
      setNewModule(blankModule);
      await queryClient.invalidateQueries({ queryKey: ["modules"] });
      await queryClient.refetchQueries({ queryKey: ["modules"] });
      const modules = queryClient.getQueryData<ModuleI[]>(["modules"]);
      const fullModule =
        created?._id && modules?.find((m) => m._id === created._id);
      if (fullModule) setModuleData?.(fullModule);
      else if (created) setModuleData?.(created);
    },
    onError: async () => {
      notify("error", "There was an error submitting the module.");
    },
  });

  const handleSubmitModule = () => {
    mutation.mutate(newModule);
  };

  const handleRemoveLesson = (lessonId: string) => {
    const newLessons = newModule.lessons.filter(
      (lesson) => lesson._id !== lessonId,
    );
    setNewModule({ ...newModule, lessons: newLessons });
  };

  return (
    <>
      <Container className="flex flex-col flex-grow max-h-[83vh] overflow-y-auto">
        <div className="flex flex-row justify-between">
          <h3 className="mb-2">New Module</h3>
          <Button
            variant="alternative"
            onClick={() => {
              const isConfirmed = confirm(
                "Are you sure you want to proceed?\nAll changes will be lost.",
              );
              if (isConfirmed) {
                window.location.reload();
              }
            }}
          >
            <span className="text-red-400">Cancel</span>
          </Button>
        </div>
        {!canCreateContent && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-700 text-sm">
              Ask an admin if you need permission to create modules.
            </p>
          </div>
        )}
        <div className={`space-y-2 ${!canCreateContent ? "opacity-60" : ""}`}>
          <Input
            label="Title"
            value={newModule.title}
            onChange={(e) =>
              setNewModule((prev) => ({ ...prev, title: e.target.value }))
            }
            className="mb-4"
            disabled={!canCreateContent}
          />
          <div className="mb-4">
            <SlugInputField
              value={newModule.slug || ""}
              onChange={(slug) => setNewModule((prev) => ({ ...prev, slug }))}
              onBlur={validateSlug}
              onResetStatus={resetSlugStatus}
              title={newModule.title}
              urlPrefix="/topics/"
              slugStatus={slugStatus}
              disabled={!canCreateContent}
            />
          </div>
          <Input
            label="Subtitle"
            value={newModule.subtitle || ""}
            onChange={(e) =>
              setNewModule((prev) => ({ ...prev, subtitle: e.target.value }))
            }
            className="mb-4"
            placeholder="e.g., Harness the Surprising Power of Gratitude"
            disabled={!canCreateContent}
          />
          <ExternalTextEditor
            label="Module Description"
            value={newModule.description}
            handleChange={(e) =>
              setNewModule((prev) => ({ ...prev, description: e }))
            }
            placeholder="Enter the module description here..."
            disabled={!canCreateContent}
          />
          <MultiSelectDropdown
            label="Categories"
            options={Object.values(CategoryEnum).map((val) => ({
              value: val,
              label: val,
            }))}
            value={newModule.categories.map((category) => ({
              value: category,
              label: category,
            }))}
            onChange={(val) => {
              setNewModule((prev) => ({
                ...prev,
                categories: val.map((c) => c.value),
              }));
            }}
            disabled={!canCreateContent}
          />
          <IconSelect
            icon={newModule.icon}
            setIcon={(icon) => setNewModule((prev) => ({ ...prev, icon }))}
            disabled={!canCreateContent}
          />
          <ColorSelect
            module={newModule}
            setModule={setNewModule}
            disabled={!canCreateContent}
          />
          <div className="flex items-center justify-between p-2">
            <Select
              options={[
                { value: ModuleStatus.Draft, label: "Draft" },
                { value: ModuleStatus.Published, label: "Published" },
                { value: ModuleStatus.Archived, label: "Archived" },
                { value: ModuleStatus.Coming_Soon, label: "Coming_Soon" },
              ]}
              value={{ value: newModule.status, label: newModule.status }}
              onChange={(option: SelectOption<ModuleStatus>) =>
                setNewModule((prev) => ({
                  ...prev,
                  published: option.value,
                }))
              }
              isDisabled={!canCreateContent}
            />
            <span
              className={`text-sm font-medium px-2 py-1 rounded-md
                ${
                  newModule.status === ModuleStatus.Published
                    ? "bg-green-100 text-green-600"
                    : newModule.status === ModuleStatus.Archived
                      ? "bg-gray-100 text-gray-600"
                      : "bg-yellow-100 text-yellow-600"
                }`}
            >
              {newModule.status
                ? newModule.status.charAt(0).toUpperCase() +
                  newModule.status.slice(1)
                : "Unpublished"}
            </span>
          </div>
          <div className="flex flex-col flex-grow justify-between overflow-auto bg-[#F5F5F5] rounded-lg p-4 space-y-2 shadow-md border border-[#E0E0E0]">
            <div>
              <h3 className="mb-2">Module Lessons</h3>
              <LessonSelect
                module={newModule}
                setModule={setNewModule}
                disabled={!canCreateContent}
              />
              <ReorderableVerticalList
                items={newModule.lessons}
                getId={(lesson) => lesson._id}
                onReorder={(lessons) =>
                  setNewModule((prev) => ({ ...prev, lessons }))
                }
                droppableId="add-topic-lessons"
                isDragDisabled={!canCreateContent}
                className="space-y-2"
              >
                {(lesson, _index, provided) => (
                  <RbdFullRowDragSurface provided={provided}>
                    <Card
                      title={lesson.title}
                      onClose={
                        canCreateContent
                          ? () => handleRemoveLesson(lesson._id)
                          : undefined
                      }
                    />
                  </RbdFullRowDragSurface>
                )}
              </ReorderableVerticalList>
            </div>
          </div>
          <Button
            className="w-full"
            onClick={() => {
              if (canCreateContent && validateModule(newModule))
                handleSubmitModule();
            }}
            disabled={!canCreateContent}
            title={
              !canCreateContent
                ? "Ask an admin if you need permission to create modules"
                : "Submit new module"
            }
          >
            Submit
          </Button>
        </div>
      </Container>
    </>
  );
}

export default AddTopicComponent;
